import * as React from 'react';
import { FormattedMessage } from 'react-intl';

import { useApolloClient, useQuery } from '@apollo/client';

import {
  DrawerContext,
  IconRegularChevronLeft,
  ModalContext,
} from '@notino/react-styleguide';
import {
  WishlistForCurrentUserQuery,
  WishlistForCurrentUserQueryVariables,
} from '@notino/shared/definitions/types';

import { DrawerProvider } from '@components/BottomSheet/DrawerProvider';
import { Visible } from '@components/Hidden';
import { WISHLIST_BASE_URL } from '@constants';
import { useTrackLoyaltyStatus } from '@containers/ProductDetailContainer/ProductDetail/hooks/useTrackLoyaltyStatus';
import { trackWishlistPageViewEvent } from '@containers/wishlistShared/tracking';
import { useWishlistSpaNavigate } from '@containers/wishlistShared/useWishlistSpaNavigate';

import { WishlistPage } from './components/WishlistPage';
import { ErrorPage } from './components/WishlistPage/ErrorPage';
import { Container } from './components/WishlistPage/styled';
import { BackLink } from './components/WishlistPage/WishlistHeader/styled';
import { WishlistSkeleton } from './components/WishlistPage/WishlistSkeleton';
import { WISHLIST_FIRST_PAGE_SIZE } from './constants';
import { useRemoveMissingProducts } from './hooks/useRemoveMissingProducts';
import { useUrlSelectionMode } from './hooks/useUrlSelectionMode';
import { useWishlistId } from './hooks/useWishlistId';
import messages from './messages';
import wishlistForCurrentUser from './queries/wishlistForCurrentUser.graphql';
import { createWishlistStore, WishlistContextProvider } from './store';

const NewWishlist = () => {
  const { sharedListId, listId } = useWishlistId();
  const { urlContainsSelectionMode } = useUrlSelectionMode();

  const { data, loading } = useQuery<
    WishlistForCurrentUserQuery,
    WishlistForCurrentUserQueryVariables
  >(wishlistForCurrentUser, {
    errorPolicy: 'all',
    fetchPolicy: 'cache-and-network',
    context: { suppressSSRErrorLogging: true },
    variables: {
      count: WISHLIST_FIRST_PAGE_SIZE,
      wishlist: {
        listId,
        wishlistId: sharedListId,
      },
      include: {
        missingProductIds: true,
      },
    },
  });

  if (loading && !data?.WishlistForCurrentUser) {
    return <WishlistSkeleton />;
  }

  if (data?.WishlistForCurrentUser) {
    const { listing, filtration, missingProductIds } =
      data.WishlistForCurrentUser;
    return (
      <NewWishlistInitialDataLoaded
        filtration={filtration}
        hasMore={listing.hasMore}
        isSharedWishlist={!!sharedListId}
        missingProductIds={missingProductIds}
        sortByOptions={listing.sortByOptions}
        selectionMode={urlContainsSelectionMode}
        totalProductCount={listing.totalProductCount}
        totalFilteredProductCount={listing.totalProductCount}
        productIds={listing.products.map((x) => x.webProductId)}
        products={listing.products.map((x) => x.product).filter(Boolean)}
      />
    );
  }

  if (sharedListId) {
    return <ErrorPage />;
  }

  return (
    <NewWishlistInitialDataLoaded
      filtration={[]}
      hasMore={false}
      isSharedWishlist={false}
      missingProductIds={[]}
      sortByOptions={[]}
      selectionMode={urlContainsSelectionMode}
      totalProductCount={0}
      totalFilteredProductCount={0}
      productIds={[]}
      products={[]}
    />
  );
};

const NewWishlistInitialDataLoaded = ({
  missingProductIds,
  ...rest
}: Parameters<typeof createWishlistStore>[0] & {
  missingProductIds: number[];
}) => {
  const { sharedListId, listId } = useWishlistId();
  const [store] = React.useState(
    createWishlistStore(rest, useApolloClient(), {
      sharedListId,
      listId,
    })
  );

  useRemoveMissingProducts({ missingProductIds, listId: listId ?? '' });

  return (
    <WishlistContextProvider value={{ store }}>
      <DrawerProvider>
        <DrawerContext.Provider>
          <ModalContext.Provider>
            <WishlistPage />
          </ModalContext.Provider>
        </DrawerContext.Provider>
      </DrawerProvider>
    </WishlistContextProvider>
  );
};

const WishList = () => {
  useTrackLoyaltyStatus();
  const wishlistNavigate = useWishlistSpaNavigate();

  // useLayoutEffect is used for tracking the page_view event to ensure the effect runs
  // before the children render, because their useEffect hooks might run after the render.
  React.useLayoutEffect(() => {
    trackWishlistPageViewEvent();
  }, []);

  return (
    <Container>
      <Visible breakpoint="md">
        <BackLink
          href={WISHLIST_BASE_URL}
          onClick={(e) => wishlistNavigate(WISHLIST_BASE_URL, e)}
        >
          <IconRegularChevronLeft
            height="1.2rem"
            width="1.2rem"
            color="icon.secondary"
          />
          <FormattedMessage {...messages.backToWishlistList} />
        </BackLink>
      </Visible>
      <NewWishlist />
    </Container>
  );
};

export default WishList;
